'use client';

import { useState } from 'react';
import { Bell, Lock, Globe, Palette, Moon, Sun, Monitor } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Select } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { useToast } from '@/components/ui/Toast';

const SettingsPage = () => {
  const { user, updateUser } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const { success } = useToast();

  const [notifications, setNotifications] = useState({
    email: user?.notification_prefs?.email ?? true,
    push: user?.notification_prefs?.push ?? true,
    sms: user?.notification_prefs?.sms ?? false,
  });

  const [language, setLanguage] = useState('en');

  const handleNotificationChange = (key: keyof typeof notifications) => {
    const newNotifications = { ...notifications, [key]: !notifications[key] };
    setNotifications(newNotifications);
    updateUser({ notification_prefs: newNotifications });
    success('Settings updated', 'Your notification preferences have been saved');
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    success('Theme updated', `Theme set to ${newTheme}`);
  };

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
  ];

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-secondary-100">Settings</h1>
        <p className="mt-1 text-gray-500 dark:text-secondary-400">Manage your account settings and preferences</p>
      </div>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-gray-400 dark:text-secondary-500" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-secondary-100">Email notifications</p>
              <p className="text-sm text-gray-500 dark:text-secondary-400">Receive updates via email</p>
            </div>
            <button
              onClick={() => handleNotificationChange('email')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notifications.email ? 'bg-primary-600' : 'bg-gray-200 dark:bg-secondary-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications.email ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-secondary-100">Push notifications</p>
              <p className="text-sm text-gray-500 dark:text-secondary-400">Receive browser push notifications</p>
            </div>
            <button
              onClick={() => handleNotificationChange('push')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notifications.push ? 'bg-primary-600' : 'bg-gray-200 dark:bg-secondary-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications.push ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-secondary-100">SMS notifications</p>
              <p className="text-sm text-gray-500 dark:text-secondary-400">Receive text message alerts</p>
            </div>
            <button
              onClick={() => handleNotificationChange('sms')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notifications.sms ? 'bg-primary-600' : 'bg-gray-200 dark:bg-secondary-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications.sms ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-gray-400 dark:text-secondary-500" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-secondary-400">Choose your preferred theme</p>
            <div className="grid grid-cols-3 gap-3">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleThemeChange(option.value as 'light' | 'dark' | 'system')}
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                      theme === option.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/20'
                        : 'border-gray-200 dark:border-secondary-700 hover:border-gray-300 dark:hover:border-secondary-600'
                    }`}
                  >
                    <Icon className={`h-6 w-6 ${
                      theme === option.value ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-secondary-500'
                    }`} />
                    <span className={`text-sm font-medium ${
                      theme === option.value ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-secondary-300'
                    }`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Language */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-gray-400 dark:text-secondary-500" />
            Language & Region
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            label="Language"
            options={languageOptions}
            value={language}
            onChange={setLanguage}
            className="max-w-xs"
          />
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-gray-400 dark:text-secondary-500" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-secondary-100">Change password</p>
              <p className="text-sm text-gray-500 dark:text-secondary-400">Update your password regularly for security</p>
            </div>
            <Button variant="outline" size="sm">
              Change
            </Button>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 dark:border-white/[0.06] pt-4">
            <div>
              <p className="font-medium text-gray-900 dark:text-secondary-100">Two-factor authentication</p>
              <p className="text-sm text-gray-500 dark:text-secondary-400">Add an extra layer of security</p>
            </div>
            <Button variant="outline" size="sm">
              Enable
            </Button>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 dark:border-white/[0.06] pt-4">
            <div>
              <p className="font-medium text-danger-600 dark:text-danger-400">Delete account</p>
              <p className="text-sm text-gray-500 dark:text-secondary-400">Permanently delete your account and data</p>
            </div>
            <Button variant="danger" size="sm">
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { SettingsPage };
